import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { ITag } from 'tag/ts/tag/interfaces/ITag';
import { IParamsVO } from 'tag/ts/tag/interfaces/params/IParamsVO';
import { EmbedDevice } from 'tag/ts/tag/model/type/embed/EmbedDevice';
import { Comcast } from 'tag/ts/tag/player/stv/configs/platforms/Comcast';
import { Cox } from 'tag/ts/tag/player/stv/configs/platforms/Cox';
import { LGNetCastConfigs } from 'tag/ts/tag/player/stv/configs/platforms/LGNetCastConfigs';
import { LGWebOSConfigs } from 'tag/ts/tag/player/stv/configs/platforms/LGWebOSConfigs';
import { NETGEM } from 'tag/ts/tag/player/stv/configs/platforms/NETGEM';
import { PlayStation4 } from 'tag/ts/tag/player/stv/configs/platforms/PlayStation4';
import { PlayStation5 } from 'tag/ts/tag/player/stv/configs/platforms/PlayStation5';
import { SAGEM } from 'tag/ts/tag/player/stv/configs/platforms/SAGEM';
import { SamsungTizen } from 'tag/ts/tag/player/stv/configs/platforms/SamsungTizen';
import { SonyHTML5 } from 'tag/ts/tag/player/stv/configs/platforms/SonyHTML5';
import { TCL } from 'tag/ts/tag/player/stv/configs/platforms/TCL';
import { TIVO } from 'tag/ts/tag/player/stv/configs/platforms/TIVO';
import { UnknownDevice } from 'tag/ts/tag/player/stv/configs/platforms/UnknownDevice';
import { VIDAA } from 'tag/ts/tag/player/stv/configs/platforms/VIDAA';
import { VizioConfigs } from 'tag/ts/tag/player/stv/configs/platforms/VizioConfigs';
import { XboxOne } from 'tag/ts/tag/player/stv/configs/platforms/XboxOne';
import { ISTVPlatformConfigsInfo, TSTVConfigConstructor } from 'tag/ts/tag/player/stv/interfaces/ISTVPlatformConfigsInfo';

const deviceConfigMap: Partial<Record<EmbedDevice, TSTVConfigConstructor>> = {
	[ EmbedDevice.LG_WEB_OS ]: LGWebOSConfigs,
	[ EmbedDevice.LG_NETCAST ]: LGNetCastConfigs,
	[ EmbedDevice.SAMSUNG_TIZEN ]: SamsungTizen,
	[ EmbedDevice.VIZIO_SMART_TV ]: VizioConfigs,
	[ EmbedDevice.SONY_HTML5 ]: SonyHTML5,
	[ EmbedDevice.SAGEM ]: SAGEM,
	[ EmbedDevice.NETGEM ]: NETGEM,
	[ EmbedDevice.XBOX_ONE ]: XboxOne,
	[ EmbedDevice.VIDAA ]: VIDAA,
	[ EmbedDevice.PS5 ]: PlayStation5,
	// Slower Devices:
	[ EmbedDevice.COMCAST ]: Comcast,
	[ EmbedDevice.COX ]: Cox,
	[ EmbedDevice.TCL ]: TCL,
	[ EmbedDevice.TIVO ]: TIVO,
	[ EmbedDevice.PS4 ]: PlayStation4,
	[ EmbedDevice.UNKNOWN ]: UnknownDevice
};

/**
 * @copyright © Paramount Streaming
 * @author  Aza Kolbaev
 * @description Smart TV platform configuration orchestrator and device-specific optimization manager
 *
 * **Platform Configuration Orchestration:**
 * STVPlatformConfigs serves as the central factory for Smart TV platform-specific configurations,
 * automatically selecting and applying optimal player and resource settings based on device detection.
 * Ensures video playback optimization across the complete Smart TV ecosystem.
 *
 * **Supported Smart TV Platforms:**
 * - **Premium Platforms**: LG webOS, Samsung Tizen, Xbox One, PlayStation 4/5, Sony HTML5
 * - **Cable/Set-Top Box**: Comcast X1, Cox, TIVO, SAGEM, NETGEM
 * - **TV Manufacturers**: Vizio, TCL, VIDAA (Hisense)
 * - **Legacy Platforms**: LG NetCast for older LG TVs
 * - **Fallback**: UnknownDevice for unrecognized platforms
 *
 * **Device Detection Strategy:**
 * Uses `EmbedDevice` classification to automatically select appropriate platform configuration:
 * - **Fast Devices**: Full feature set with advanced codec support
 * - **Slower Devices**: Optimized settings for constrained hardware (Comcast, Cox, TCL, etc.)
 * - **Gaming Consoles**: Platform-specific optimizations for Xbox/PlayStation environments
 *
 * **Configuration Factory Pattern:**
 * ```typescript
 * const device = params.SYSTEM.device; // EmbedDevice.SAMSUNG_TIZEN
 * const ConfigClass = deviceConfigMap[device] ?? UnknownDevice;
 * return new ConfigClass(params).configs; // Returns Samsung-optimized configuration
 * ```
 *
 * **Business Impact:**
 * Critical for Smart TV deployment success, ensuring optimal video performance across
 * diverse hardware capabilities while maintaining consistent Smart Tag functionality.
 */
export class STVPlatformConfigs extends AbstractTagClass {

	#platformConfigs: ISTVPlatformConfigsInfo;

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'STVPlatformConfigs'
			}
		);
		this.#platformConfigs = STVPlatformConfigs.setConfigs( this?.params, this?.tag );
	}

	/**
	 * @description Setup platform specific configs
	 * @param {IParamsVO} params
	 * @param {ITag} tag
	 * @returns {any}
	 */
	static setConfigs( params: IParamsVO, tag?: ITag ): ISTVPlatformConfigsInfo {
		const device = params?.SYSTEM?.device ?? EmbedDevice.UNKNOWN;
		const configClass = deviceConfigMap[ device ] ?? UnknownDevice;
		return new configClass( params, tag ).configs;
	}

	/**
	 * @description Gets resourceConfig
	 * @returns {ISTVPlatformConfigsInfo.resourceConfig}
	 * */
	get resourceConfig(): ISTVPlatformConfigsInfo['resourceConfig'] {
		return this.#platformConfigs?.resourceConfig;
	}

	/**
	 * @description Gets playerOptions
	 * @returns {ISTVPlatformConfigsInfo.playerOptions}
	 * */
	get playerOptions(): ISTVPlatformConfigsInfo['playerOptions'] {
		return this.#platformConfigs?.playerOptions;
	}
}